import type { JournalEntry } from '@erp/shared';
import { BookOpen, Pencil, Printer, Trash2 } from 'lucide-react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

import { Badge, Card, Input, Skeleton } from '../../components/shadcn/index.js';
import {
  Button,
  confirmDialog,
  DataTable,
  type DataTableColumn,
  EmptyState,
} from '../../components/ui/index.js';
import { usePermission } from '../../hooks/usePermission.js';
import { api } from '../../lib/api.js';
import { useAuthStore } from '../../lib/authStore.js';
import { appUrl } from '../../lib/basePath.js';
import { formatDate } from '../../lib/intl.js';
import { formatMoney } from '../../lib/money.js';

/** Total debits of an entry, in the tenant base currency (both sides equal). */
function entryBaseTotal(e: JournalEntry): number {
  return e.lines.reduce((s, l) => s + Number(l.baseDebit), 0);
}
/** Total debits in the entry's own currency (single-currency entries only). */
function entryTxnTotal(e: JournalEntry): number {
  return e.lines.reduce((s, l) => s + Number(l.debit), 0);
}
/** The entry's transaction currency when all lines share one, else null (mixed). */
function entryCurrency(e: JournalEntry): string | null {
  const set = new Set(e.lines.map((l) => l.transactionCurrencyCode));
  return set.size === 1 ? [...set][0]! : null;
}

/** One request's worth of entries — the endpoint's own maximum. */
const JOURNAL_PAGE = 500;

export function JournalsPage(): JSX.Element {
  const { t, i18n } = useTranslation('gl');
  const { t: tc } = useTranslation();
  const canPost = usePermission('gl.post');
  const canReverse = usePermission('gl.reverse');
  const baseCurrency = useAuthStore((s) => s.tenant?.baseCurrencyCode) ?? 'USD';
  const [entries, setEntries] = useState<JournalEntry[] | null>(null);
  const [from, setFrom] = useState('');
  const [to, setTo] = useState('');
  const [source, setSource] = useState('');
  const [statusFilter, setStatusFilter] = useState('');

  /** While more pages are still arriving: how many are in, of how many. */
  const [loading, setLoading] = useState<{ have: number; total: number } | null>(null);
  /** Bumped per load, so a slow page from earlier dates can't land on newer ones. */
  const loadSeq = useRef(0);
  /** The dates the list on screen was loaded for — a new range fills in as it arrives. */
  const loadedFor = useRef<string | null>(null);

  /**
   * EVERY entry, not the newest 500. The page asked for one page of 500 and stopped
   * (Duhok has 4,000+ entries), so anything older simply was not there. It now pages
   * through the server's maximum of 500 at a time. A new date range fills in page by
   * page; a reload after posting or reversing keeps the list on screen until the
   * fresh one is complete, so the table does not jump.
   */
  const reload = async (): Promise<void> => {
    const seq = ++loadSeq.current;
    const key = `${from}|${to}`;
    const progressive = loadedFor.current !== key;
    const rows: JournalEntry[] = [];
    try {
      for (let offset = 0; ; offset += JOURNAL_PAGE) {
        const params = new URLSearchParams({ limit: String(JOURNAL_PAGE), offset: String(offset) });
        if (from) params.set('from', from);
        if (to) params.set('to', to);
        const r = await api().get<{ items: JournalEntry[]; total?: number }>(`/journal-entries?${params.toString()}`);
        if (seq !== loadSeq.current) return; // the dates changed under us
        rows.push(...r.data.items);
        const total = r.data.total ?? rows.length;
        const done = r.data.items.length < JOURNAL_PAGE || rows.length >= total;
        if (progressive || done) setEntries([...rows]);
        setLoading(done ? null : { have: rows.length, total });
        if (done) { loadedFor.current = key; return; }
      }
    } catch (e) {
      if (seq === loadSeq.current) {
        setLoading(null);
        if (rows.length) setEntries([...rows]);
      }
      throw e;
    }
  };
  useEffect(() => {
    void reload();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [from, to]);

  // Distinct source types present, for the source filter dropdown.
  const sources = useMemo(() => {
    const set = new Set<string>();
    for (const e of entries ?? []) set.add(e.sourceType || 'manual');
    return [...set].sort();
  }, [entries]);

  // Client-side source + status filtering over the (date-filtered) server list.
  const shown = useMemo(() => {
    return (entries ?? []).filter((e) => {
      const src = e.sourceType || 'manual';
      if (source && src !== source) return false;
      if (statusFilter === 'reversal' && !e.reversesId) return false;
      if (statusFilter === 'reversed' && !e.reversedById) return false;
      if (statusFilter === 'posted' && (e.reversesId || e.reversedById)) return false;
      return true;
    });
  }, [entries, source, statusFilter]);

  const postReverse = async (id: string, message: string): Promise<void> => {
    if (!(await confirmDialog({ message, tone: 'danger' }))) return;
    try {
      await api().post(`/journal-entries/${id}/reverse`, {});
      await reload();
    } catch (e) {
      const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
      toast.error(code === 'conflict' ? t('journals.errors.alreadyReversed') : tc('errors:server'));
    }
  };

  const onReverse = (id: string, entryNumber: string): Promise<void> =>
    postReverse(id, t('journals.actions.confirmReverse', { entryNumber, defaultValue: `Reverse ${entryNumber}?` }));

  // A manual entry has no source document (or an explicit 'manual' source).
  const isManual = (e: JournalEntry): boolean => !e.sourceType || e.sourceType === 'manual';
  // A reversal entry (it undid some other entry).
  const isReversal = (e: JournalEntry): boolean => !!e.reversesId;
  // "Active" = not already reversed and not itself a reversal.
  const isActive = (e: JournalEntry): boolean => !e.reversedById && !e.reversesId;
  // Manual + active entries can be edited in place or hard-deleted outright.
  const isManualEditable = (e: JournalEntry): boolean => canPost && isManual(e) && isActive(e);
  // A row can be REMOVED when:
  //  - active manual → hard delete (gl.post)
  //  - active source-backed → balancing reversal, since its document FK-locks the JE (gl.reverse)
  //  - a reversal → delete it to UNDO the reversal, restoring the original (gl.post)
  const canRemove = (e: JournalEntry): boolean =>
    (isActive(e) && (isManualEditable(e) || (canReverse && !isManual(e)))) ||
    (isReversal(e) && canPost);

  const onHardDelete = async (id: string, entryNumber: string, reversal = false): Promise<void> => {
    const message = reversal
      ? t('journals.actions.confirmDeleteReversal', { entryNumber, defaultValue: `Delete reversal ${entryNumber}? This removes it and restores the entry it reversed to active.` })
      : t('journals.actions.confirmDelete', { entryNumber, defaultValue: `Delete ${entryNumber}? This permanently removes the entry.` });
    if (!(await confirmDialog({ message, tone: 'danger' }))) return;
    try {
      await api().delete(`/journal-entries/${id}`);
      await reload();
    } catch (e) {
      const cause = (e as { response?: { data?: { cause?: string } } }).response?.data?.cause;
      toast.error(cause === 'period_closed' ? t('manualEntry.errors.periodClosed', { defaultValue: 'That entry is in a closed period.' }) : tc('errors:server'));
    }
  };

  // The "Delete" affordance. A reversal is dropped (undoing it); a manual entry is
  // erased; a source-backed entry can't be (its document RESTRICT-locks the GL
  // entry) so it's cancelled with a balancing reversal instead.
  const onDelete = (e: JournalEntry): Promise<void> =>
    isReversal(e)
      ? onHardDelete(e.id, e.entryNumber, true)
      : isManual(e)
        ? onHardDelete(e.id, e.entryNumber)
        : postReverse(e.id, t('journals.actions.confirmDeleteViaReverse', { entryNumber: e.entryNumber, source: e.sourceType, defaultValue: `${e.entryNumber} came from ${e.sourceType} and can't be erased — post a balancing reversal to cancel its effect?` }));

  if (!entries) return <Card className="p-6"><Skeleton className="h-32 w-full" /></Card>;
  const header = (
    <header className="flex items-start justify-between gap-4">
      <div>
        <h1 className="text-2xl font-semibold">{t('journals.title')}</h1>
        <p className="mt-1 text-sm text-fg-muted">{t('journals.description')}</p>
      </div>
      {canPost && (
        <Link to="/accounting/journals/new"><Button>{t('manualEntry.title')}</Button></Link>
      )}
    </header>
  );
  if (entries.length === 0 && !from && !to) {
    return (
      <div className="flex flex-col gap-4">
        {header}
        <EmptyState
          icon={<BookOpen className="h-8 w-8" aria-hidden />}
          title={t('journals.empty')}
          description={t('journals.emptyDescription')}
          action={
            canPost ? (
              <Link to="/accounting/journals/new">
                <Button size="sm">{t('manualEntry.title')}</Button>
              </Link>
            ) : undefined
          }
        />
      </div>
    );
  }

  const columns: DataTableColumn<JournalEntry>[] = [
    {
      id: 'entryNumber',
      header: t('journals.columns.entryNumber'),
      sortValue: (e) => e.entryNumber,
      searchValue: (e) => e.entryNumber,
      cellClassName: 'font-mono text-xs',
      cell: (e) => e.entryNumber,
    },
    {
      id: 'entryDate',
      header: t('journals.columns.entryDate'),
      sortValue: (e) => e.entryDate,
      cell: (e) => formatDate(e.entryDate, { locale: i18n.language }),
    },
    {
      id: 'description',
      header: t('journals.columns.description'),
      sortValue: (e) => e.description ?? '',
      searchValue: (e) => e.description ?? '',
      cellClassName: 'font-medium',
      cell: (e) => e.description ?? '—',
    },
    {
      id: 'source',
      header: t('journals.columns.source'),
      sortValue: (e) => e.sourceType ?? '',
      cell: (e) =>
        e.sourceType ? (
          <code className="rounded bg-bg-subtle px-1.5 py-0.5 font-mono text-[0.65rem] text-fg-muted">
            {e.sourceType}
          </code>
        ) : (
          <span className="text-xs text-fg-muted">{t('journals.source.manual')}</span>
        ),
    },
    {
      id: 'amount',
      header: t('journals.columns.amount', { defaultValue: 'Amount' }),
      align: 'end',
      sortValue: (e) => entryBaseTotal(e),
      cellClassName: 'font-mono tabular-nums',
      // In the entry's OWN currency when it has one: a 100 USD entry reads
      // "$100.00", with its base equivalent under it — not "IQD 148,000" next to
      // a "USD" column. Mixed entries only add up in base.
      cell: (e) => {
        const ccy = entryCurrency(e);
        const base = formatMoney(entryBaseTotal(e).toFixed(4), { locale: i18n.language, currency: baseCurrency });
        if (!ccy || ccy === baseCurrency) return base;
        return (
          <span className="inline-flex flex-col items-end leading-tight">
            <span>{formatMoney(entryTxnTotal(e).toFixed(4), { locale: i18n.language, currency: ccy })}</span>
            <span className="text-[0.7rem] text-fg-muted">≈ {base}</span>
          </span>
        );
      },
    },
    {
      id: 'currency',
      header: t('journals.columns.currency', { defaultValue: 'Currency' }),
      align: 'end',
      sortValue: (e) => entryCurrency(e) ?? 'ZZZ',
      cellClassName: 'font-mono text-xs text-fg-muted',
      cell: (e) => entryCurrency(e) ?? <span title={t('journals.mixedCurrency', { defaultValue: 'Multiple currencies' })}>{t('journals.mixed', { defaultValue: 'Mixed' })}</span>,
    },
    {
      id: 'status',
      header: t('journals.columns.status'),
      cell: (e) =>
        e.reversesId ? (
          <Badge variant="warning">{t('journals.status.reversal')}</Badge>
        ) : e.reversedById ? (
          <Badge variant="secondary">{t('journals.status.reversed')}</Badge>
        ) : (
          <Badge variant="success">{t('journals.status.posted')}</Badge>
        ),
    },
    {
      id: 'lines',
      header: t('journals.columns.lines'),
      align: 'end',
      sortValue: (e) => Number(e.lines.length),
      cellClassName: 'font-mono tabular-nums',
      cell: (e) => e.lines.length,
    },
    {
      id: 'actions',
      header: '',
      align: 'end' as const,
      headClassName: 'w-52',
      // window.open (not <a target="_blank">) so the print tab keeps the opener
      // and stays signed in (anchors get implicit noopener → bounce to login).
      cell: (e: JournalEntry) => (
        <div className="flex justify-end gap-1">
          <Button
            variant="ghost"
            size="sm"
            aria-label={`${tc('actions.print', { defaultValue: 'Print' })} ${e.entryNumber}`}
            title={tc('actions.print', { defaultValue: 'Print' })}
            onClick={() => window.open(appUrl(`/accounting/journals/${e.id}/print`), '_blank')}
          >
            <Printer className="h-4 w-4" aria-hidden />
          </Button>
          {isManualEditable(e) && (
            <Link to={`/accounting/journals/${e.id}/edit`}>
              <Button variant="ghost" size="sm" aria-label={`${tc('actions.edit', { defaultValue: 'Edit' })} ${e.entryNumber}`} title={tc('actions.edit', { defaultValue: 'Edit' })}>
                <Pencil className="h-4 w-4" aria-hidden />
              </Button>
            </Link>
          )}
          {canRemove(e) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onDelete(e)}
              aria-label={`${tc('actions.delete', { defaultValue: 'Delete' })} ${e.entryNumber}`}
              title={isManual(e) || isReversal(e) ? tc('actions.delete', { defaultValue: 'Delete' }) : t('journals.actions.deleteViaReverse', { defaultValue: 'Delete (posts a reversal)' })}
            >
              <Trash2 className="h-4 w-4" aria-hidden />
            </Button>
          )}
          {canReverse && isActive(e) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => onReverse(e.id, e.entryNumber)}
            >
              {t('journals.actions.reverse', { defaultValue: 'Reverse' })}
            </Button>
          )}
        </div>
      ),
    },
  ];

  return (
    <div className="flex flex-col gap-4">
      {header}
      <Card className="flex flex-wrap items-center gap-3 p-3">
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('journals.filters.from', { defaultValue: 'From' })}
          <Input type="date" value={from} max={to || undefined} onChange={(e) => setFrom(e.target.value)} className="h-9 w-auto" />
        </label>
        <label className="flex items-center gap-1.5 text-xs text-fg-muted">
          {t('journals.filters.to', { defaultValue: 'To' })}
          <Input type="date" value={to} min={from || undefined} onChange={(e) => setTo(e.target.value)} className="h-9 w-auto" />
        </label>
        <select
          className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-fg"
          value={source}
          onChange={(e) => setSource(e.target.value)}
          aria-label={t('journals.filters.source', { defaultValue: 'Source' })}
        >
          <option value="">{t('journals.filters.allSources', { defaultValue: 'All sources' })}</option>
          {sources.map((s) => (
            <option key={s} value={s}>{s === 'manual' ? t('journals.source.manual') : s}</option>
          ))}
        </select>
        <select
          className="h-9 rounded-md border border-border bg-bg px-3 text-sm text-fg"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          aria-label={t('journals.columns.status')}
        >
          <option value="">{t('journals.filters.allStatuses', { defaultValue: 'All statuses' })}</option>
          <option value="posted">{t('journals.status.posted')}</option>
          <option value="reversed">{t('journals.status.reversed')}</option>
          <option value="reversal">{t('journals.status.reversal')}</option>
        </select>
        {(from || to || source || statusFilter) && (
          <Button variant="ghost" size="sm" onClick={() => { setFrom(''); setTo(''); setSource(''); setStatusFilter(''); }}>
            {t('journals.filters.clear', { defaultValue: 'Clear' })}
          </Button>
        )}
        <span className="ms-auto text-xs text-fg-muted">
          {loading
            ? t('journals.loading', { have: loading.have, total: loading.total, defaultValue: 'Loading {{have}} of {{total}}…' })
            : t('journals.filters.showing', { shown: shown.length, total: entries.length, defaultValue: `${shown.length} of ${entries.length}` })}
        </span>
      </Card>
      <Card className="overflow-hidden p-0">
        <DataTable columns={columns} data={shown} rowKey={(e) => e.id} />
      </Card>
    </div>
  );
}
